import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home, ShieldCheck, Clock } from "lucide-react";
import { api, eur, frDate } from "@/lib/api";
import { useI18n } from "@/i18n";
import { PAY } from "@/i18n/pay";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });
  const { lang } = useI18n();
  const p = PAY[lang] || PAY.fr;
  const isDeposit = state.data?.kind === "deposit";

  useEffect(() => {
    if (!sessionId) return setState({ status: "error" });
    let tries = 0;
    let timer;
    let last = null;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        last = data;
        if (data.payment_status === "paid") return setState({ status: "paid", data });
        if (data.payment_status === "authorized") return setState({ status: "authorized", data });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ status: "failed", data });
      } catch {
        return setState({ status: "error" });
      }
      if (++tries > 10) return setState({ status: "authorized", data: last });
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="payment-success-page">
      {state.status === "checking" && (
        <>
          <Loader2 className="mx-auto animate-spin text-bone" size={40} />
          <h1 className="mt-6 font-display text-3xl text-bone">{p.checking}</h1>
          <p className="mt-3 text-sm text-muted-foreground">{p.checkingNote}</p>
        </>
      )}

      {(state.status === "paid" || state.status === "authorized") && (
        <div data-testid={state.status === "authorized" ? "payment-authorized" : "payment-confirmed"}>
          {state.status === "authorized"
            ? <ShieldCheck className="mx-auto text-bone" size={48} />
            : <CheckCircle2 className="mx-auto text-bone" size={48} />}
          <h1 className="mt-6 font-display text-4xl text-bone">
            {isDeposit ? p.depositTitle : state.status === "authorized" ? p.authorizedTitle : p.confirmed}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {p.thanks} {state.data?.booking?.name}.{" "}
            {isDeposit ? p.depositNote : state.status === "authorized" ? p.authorizedNote : p.confirmedNote}
          </p>
          {state.data?.booking && (
            <div className="panel mx-auto mt-10 max-w-md space-y-3 p-8 text-left text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{p.arrival}</span><span>{frDate(state.data.booking.start_date)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{p.departure}</span><span>{frDate(state.data.booking.end_date)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{p.nights}</span><span>{state.data.booking.nights}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{p.guests}</span><span>{state.data.booking.guests}</span></div>
              <div className="flex justify-between border-t border-white/10 pt-3 font-medium text-bone">
                <span>{isDeposit ? p.depositHeld : state.status === "authorized" ? p.held : p.paid}</span><span>{eur(state.data.amount)}</span>
              </div>
            </div>
          )}
          <Link to="/" className="btn-primary mt-10" data-testid="success-home-btn"><Home size={16} /> {p.home}</Link>
        </div>
      )}

      {state.status === "pending" && (
        <div data-testid="payment-pending">
          <Clock className="mx-auto text-bone" size={44} />
          <h1 className="mt-6 font-display text-3xl text-bone">{p.pendingTitle}</h1>
          <p className="mt-4 text-sm text-muted-foreground">{p.pendingNote}</p>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/" className="btn-primary" data-testid="pending-home-btn">{p.home}</Link>
            <Link to="/contact" className="btn-ghost">{p.contact}</Link>
          </div>
        </div>
      )}

      {["failed", "error"].includes(state.status) && (
        <div data-testid="payment-issue">
          <XCircle className="mx-auto text-destructive" size={44} />
          <h1 className="mt-6 font-display text-3xl text-bone">
            {p.failedTitle}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {p.failedNote}
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/reservation" className="btn-primary">{p.retry}</Link>
            <Link to="/contact" className="btn-ghost">{p.contact}</Link>
          </div>
        </div>
      )}
    </div>
  );
}
